<script setup lang="ts">

import { CDN } from '@/models/cdn.ts'
import BuildingEffects from '@/components/Sections/Planets/PlanetTabs/Generic/BuildingEffects.vue'
import DeleteButton from '@/components/Elements/Buttons/DeleteButton.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import ModalTrigger from '@/components/Interface/Modal/ModalTrigger.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { usePlanetStore } from '@/stores/planetStore.ts'
import { useShipDesignStore } from '@/stores/shipDesignStore.ts'
import { computed, defineProps } from 'vue'
import Backend from '@/models/backend.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  },
  queueId: {
    type: Number,
    required: true
  },
  productionObjectId: {
    type: Number,
    required: true
  },
  productionShipDesignId: {
    type: Number,
    required: true
  },
  productionShipFleetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const assetStore = useAssetStore()
const gameStore = useGameStore()
const planetStore = usePlanetStore()
const fleetStore = useFleetStore()
const shipDesignStore = useShipDesignStore()
const backend = new Backend()

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const productionObjectData = computed(() => assetStore.findProductionObjectById(props.productionObjectId))

const shipDesignData = computed(() => shipDesignStore.getShipDesignById(props.productionShipDesignId))

const name = computed(() => {
  if (shipDesignData.value) {
    return shipDesignData.value.name
  } else if (productionObjectData.value.type === 'building') {
    return t(`buildings.buildings.${productionObjectData.value.objectId}.name`)
  } else {
    return t('planets.production.ship.name')
  }
});

const buildingData = computed(() => {
  return assetStore.findBuildingByCode(productionObjectData.value.objectId)
})

// Get full queue including current production
const fullQueue = computed(() => {
  const items = [];

  // Add current production as first item if it exists
  if (planetData.value.productionObjectId > 0) {
    items.push({
      id: 0,
      productionObjectId: planetData.value.productionObjectId,
      productionShipDesignId: planetData.value.productionShipDesignId,
      productionShipFleetId: planetData.value.productionShipFleetId
    });
  }

  // Add queued items
  if (planetData.value.productionQueue) {
    items.push(...planetData.value.productionQueue);
  }

  return items;
});

// Find position in full queue
const queuePosition = computed(() => {
  return fullQueue.value.findIndex(item => item.id === props.queueId);
});

// Check if can move up
const canMoveUp = computed(() => queuePosition.value > 0);

// Check if can move down
const canMoveDown = computed(() => {
  return queuePosition.value >= 0 && queuePosition.value < fullQueue.value.length - 1;
});

/**
 * Methods
 */
const deleteFromQueue = () => {
  backend.removeProductionObjectFromQueue(props.planetId, props.queueId);
  gameStore.setTinySuccessToastMessage(t('planets.production.current.removedFromQueue'));
}

const moveUp = () => {
  const currentQueue = [...fullQueue.value];
  const index = queuePosition.value;

  if (index > 0) {
    [currentQueue[index], currentQueue[index - 1]] = [currentQueue[index - 1], currentQueue[index]];
    reorderQueue(currentQueue);
  }
}

const moveDown = () => {
  const currentQueue = [...fullQueue.value];
  const index = queuePosition.value;

  if (index >= 0 && index < currentQueue.length - 1) {
    [currentQueue[index], currentQueue[index + 1]] = [currentQueue[index + 1], currentQueue[index]];
    reorderQueue(currentQueue);
  }
}

const moveToTop = () => {
  const currentQueue = [...fullQueue.value];
  const index = queuePosition.value;

  if (index > 0) {
    const item = currentQueue.splice(index, 1)[0];
    currentQueue.unshift(item);
    reorderQueue(currentQueue);
  }
}

const moveToBottom = () => {
  const currentQueue = [...fullQueue.value];
  const index = queuePosition.value;

  if (index >= 0 && index < currentQueue.length - 1) {
    const item = currentQueue.splice(index, 1)[0];
    currentQueue.push(item);
    reorderQueue(currentQueue);
  }
}

const reorderQueue = async (reorderedQueue: any[]) => {
  const apiQueue = reorderedQueue.map(item => ({
    id: item.id,
    production_object_id: item.productionObjectId,
    production_ship_design_id: item.productionShipDesignId,
    production_ship_fleet_id: item.productionShipFleetId
  }));

  try {
    await backend.reorderPlanetProductionQueue(planetData.value.id, apiQueue);
    gameStore.setTinySuccessToastMessage(t('planets.production.current.reordered'));
  } catch (error) {
    gameStore.setTinyErrorToastMessage(t('planets.production.current.reorderFailed'));
    logger.error('Failed to reorder production queue:', error);
  }
}

</script>

<template>
  <StandardCard :title="name" bottom-padding="0" top-padding="2">
    <div class="flex flex-col">
      <!--- Image --->
      <div class="flex justify-between">
        <div class="flex-shrink-0">
        <ModalTrigger v-if="productionObjectData.type=='building'" type="building" :data="{'buildingCode': productionObjectData.objectId}">
          <StyledImage :src="CDN.building(128,productionObjectData.objectId)" size="card" />
        </ModalTrigger>
        <StyledImage
          v-else-if="shipDesignData?.frameCode"
          :src="CDN.shipComponent(128, shipDesignData.frameCode)"
          size="card"
        />
        </div>
        <div class="flex flex-grow flex-col">
          <div class="flex-grow text-subtext ps-2">
            <span v-if="productionShipFleetId>0" class="pt-3" v-html="t('planets.production.current.buildShipForFleet', { fleetName: `<b class='text-metal-400'>${fleetStore.getFleetNameById(productionShipFleetId)}</b>` })"></span>
            <span v-else-if="productionObjectData.type === 'building'">{{ t(`buildings.buildings.${productionObjectData.objectId}.description`) }}</span>
          </div>
          <!--- Icons --->
          <div class="flex justify-between">
            <div class="flex justify-start">
              <div class="flex items-center pt-1 ps-2">
                <BuildingEffects v-if="buildingData" :building="buildingData"/>
              </div>
            </div>
            <div class="flex items-end gap-1">
              <ActionButton
                @click.stop="moveToTop"
                :disabled="!canMoveUp"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveUp }"
                :for-icon="true"
                :title="t('planets.production.queue.moveToTop')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveToTop')" type="top" />
              </ActionButton>
              <ActionButton
                @click.stop="moveUp"
                :disabled="!canMoveUp"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveUp }"
                :for-icon="true"
                :title="t('planets.production.queue.moveUp')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveUp')" type="up" />
              </ActionButton>
              <ActionButton
                @click.stop="moveDown"
                :disabled="!canMoveDown"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
                :for-icon="true"
                :title="t('planets.production.queue.moveDown')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveDown')" type="down" />
              </ActionButton>
              <ActionButton
                @click.stop="moveToBottom"
                :disabled="!canMoveDown"
                :class="{ 'opacity-50 cursor-not-allowed': !canMoveDown }"
                :for-icon="true"
                :title="t('planets.production.queue.moveToBottom')"
              >
                <GenericIcon :tooltip="t('planets.production.queue.moveToBottom')" type="bottom" />
              </ActionButton>
              <DeleteButton @click="deleteFromQueue" :for-icon="true">
                <GenericIcon :tooltip="t('planets.production.queue.delete')" type="trash" />
              </DeleteButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">

</style>
